import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate';
import auth from "../auth.js"
import { getDefaultBottleSize, getProductUnitPrice, isBottleSizeOptionEnabled, isSprayRollOnOptionEnabled } from "@/utils/productOptions.js"

const getCartVariantKey = (product, choice, size) => {
  const parts = [product.id];

  if (isSprayRollOnOptionEnabled(product)) {
    parts.push(choice || 'Spray');
  }

  if (isBottleSizeOptionEnabled(product)) {
    parts.push(size || getDefaultBottleSize(product));
  }

  return parts.join('__');
};

export default createStore({
  state: {
    cart: {},
    categories: [],
    items:[],
    isLoggedIn: localStorage.getItem('isLoggedIn') || false,
    appLoading: false,
    showMessage: false,
    product: null,
    products: [],
    post: null,
    selectedLang: localStorage.getItem('selectedLang') || require('@/assets/images/flag-estonia.svg'),
    locale: localStorage.getItem('locale') || 'et'
  },
  mutations: {
    addToCart(state, { product, quantity, choice = 'Spray', size = getDefaultBottleSize() }) {
      const hasSprayRollOnOption = isSprayRollOnOptionEnabled(product);
      const hasBottleSizeOption = isBottleSizeOptionEnabled(product);
      const normalizedChoice = hasSprayRollOnOption ? (choice || 'Spray') : null;
      const normalizedSize = hasBottleSizeOption ? (size || getDefaultBottleSize(product)) : null;
      const cartKey = getCartVariantKey(product, normalizedChoice, normalizedSize);

      if (state.cart[cartKey]) {
        state.cart[cartKey].quantity += quantity;
      } else {
        state.cart[cartKey] = { product, quantity, choice: normalizedChoice, size: normalizedSize };
      }
    },
    removeFromCart(state, productId) {
      delete state.cart[productId];
    },
    increaseQuantity(state, productId) {
      if (state.cart[productId]) {
        state.cart[productId].quantity = Number(state.cart[productId].quantity) + 1;
      }
    },
    decreaseQuantity(state, productId) {
      if (state.cart[productId]) {
        if (state.cart[productId].quantity > 1) {
          state.cart[productId].quantity -= 1;
        } else {
          this.commit('removeFromCart', productId);
        }
      }
    },
    deleteCart(state) {
      state.cart = {};
    },
    loadCart(state, cart) {
      if (cart && typeof cart === 'object' && !Array.isArray(cart)) {
        state.cart = cart;
        return;
      }

      state.cart = {};
    },
    SET_LOGIN_STATUS(state, status) {
      if (status === false) localStorage.removeItem('isLoggedIn');
      else localStorage.setItem('isLoggedIn', status);
      state.isLoggedIn = status;
    },
    SET_APP_LOADING(state, status) {
      state.appLoading = Boolean(status);
    },
    SET_TOAST_MESSAGE(state, message) {
      state.showMessage = message;
    },
    updateQuantity(state, { productId, quantity }) {
      if (state.cart[productId]) {
        state.cart[productId].quantity = quantity;
      }
    },
    updateCartChoice(state, { productId, choice }) {
      const cartItem = state.cart[productId];
      const hasSprayRollOnOption = isSprayRollOnOptionEnabled(cartItem?.product);
      const hasBottleSizeOption = isBottleSizeOptionEnabled(cartItem?.product);

      if (!cartItem || !choice || !hasSprayRollOnOption) {
        return;
      }

      const newKey = getCartVariantKey(cartItem.product, choice, hasBottleSizeOption ? cartItem.size : null);

      if (newKey === productId) {
        state.cart[productId].choice = choice;
        return;
      }

      if (state.cart[newKey]) {
        state.cart[newKey].quantity += Number(cartItem.quantity);
        delete state.cart[productId];
        return;
      }

      // Rebuild cart in one pass: replace old key with new key at the same position.
      const newCart = {};
      for (const k of Object.keys(state.cart)) {
        if (k === productId) {
          newCart[newKey] = { ...cartItem, choice };
        } else {
          newCart[k] = state.cart[k];
        }
      }
      state.cart = newCart;
    },
    updateCartSize(state, { productId, size }) {
      const cartItem = state.cart[productId];
      const hasBottleSizeOption = isBottleSizeOptionEnabled(cartItem?.product);

      if (!cartItem || !size || !hasBottleSizeOption) {
        return;
      }

      const newKey = getCartVariantKey(cartItem.product, cartItem.choice, size);

      if (newKey === productId) {
        state.cart[productId].size = size;
        return;
      }

      if (state.cart[newKey]) {
        state.cart[newKey].quantity += Number(cartItem.quantity);
        delete state.cart[productId];
        return;
      }

      // Rebuild cart in one pass: replace old key with new key at the same position.
      const newCart = {};
      for (const k of Object.keys(state.cart)) {
        if (k === productId) {
          newCart[newKey] = { ...cartItem, size };
        } else {
          newCart[k] = state.cart[k];
        }
      }
      state.cart = newCart;
    },
    setCategories(state, categories) {
      state.categories = categories
    },
    setProducts(state, products) {
      state.products = products;
    },
    setProduct(state, product) {
      state.product = product;
    },
    setPost(state, post) {
      state.post = post;
    },
    setUser(state, user) {
      state.user = user;
    }
  },
  actions: {
    addToCart(context, { product, quantity, choice = 'Spray', size = getDefaultBottleSize() }) {
      context.commit('addToCart', { product, quantity, choice, size });
    },
    removeFromCart(context, productId) {
      context.commit('removeFromCart', productId);
    },
    async checkLoginStatus({ commit }) {
      try {
        const isAuthenticated = await auth.authenticated();
        commit('SET_LOGIN_STATUS', isAuthenticated);
      } catch (e) {
        console.log(e);
        console.log("error checking login status");
      }
    },
    fetchProducts({ commit }) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/products`)
          .then((response) => response.json())
          .then((data) => commit('setProducts', data))
          .catch((err) => console.log(err.message));
    },
    async fetchProduct({ commit }, id) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/products/${id}`)
          .then((response) => response.json())
          .then((data) => commit('setProduct', data))
          .catch((err) => console.log(err.message));
    },
    async fetchPost({ commit }, id) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/posts/${id}`)
          .then((response) => response.json())
          .then((data) => commit('setPost', data))
          .catch((err) => console.log(err.message));
    },
    fetchCategories({ commit }) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/categories`)
          .then((response) => response.json())
          .then((data) => commit('setCategories', data))
          .catch((err) => console.log(err.message));
    }
  },
  getters: {
    cartItemCount(state) {
      return Object.keys(state.cart).length;
    },
    cartTotal: state => {
      return Object.values(state.cart).reduce((total, item) => {
        const price = getProductUnitPrice(item.product, item.size);
        return total + (price * item.quantity);
      }, 0);
    },
    getProducts(state) {
      return state.products;
    },
    getProduct(state) {
      return state.product;
    },
    getUser(state) {
      return state.user;
    },
    getPost(state) {
      return state.post;
    },
    getLang(state) {
      return state.locale;
    },
    getSelectedLang(state) {
      return state.selectedLang;
    },
    getCategories(state) {
      return state.categories;
    },
    formatDate: (state) => (dateString) => {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },
    getLinkCat: (state) => (content) => {
      let id = Array.isArray(content?.category_ids) ? content.category_ids[0] : null;
      let category = state.categories.find(cat => cat.id === id);
      if (!category) return '';

      const categoryName = category.name || category.cat || '';
      const parts = categoryName
          .split(' - ')
          .map((part) => part.trim())
          .filter(Boolean);

      const slugifyPart = (part) => part
          .toLowerCase()
          .replace(/[ä]/g, 'a')
          .replace(/[öõ]/g, 'o')
          .replace(/[ü]/g, 'u')
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');

      return parts.map(slugifyPart).filter(Boolean).join('/');
    }
  },
  modules: {
  },
  plugins: [createPersistedState({ storage: window.sessionStorage })]
})
